import { SessionExpiredError } from '@/lib/session-expired-error'
import type { ApplicationInsights } from '@microsoft/applicationinsights-web'

let appInsights: ApplicationInsights | null = null
let readyPromise: Promise<void> | null = null
let initialized = false
let authenticatedUserId: string | null = null

function getQuoteIdSafe(): string | undefined {
  return new URLSearchParams(window.location.search).get('id') ?? undefined
}

function withQuoteId(properties?: Record<string, string>): Record<string, string> {
  const quoteId = getQuoteIdSafe()
  return { ...(quoteId && { quoteId }), ...properties }
}

export function initTelemetry() {
  if (initialized) return
  initialized = true

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined
  if (!connectionString) return

  readyPromise = Promise.all([
    import('@microsoft/applicationinsights-web'),
    import('@microsoft/applicationinsights-clickanalytics-js'),
  ])
    .then(([{ ApplicationInsights }, { ClickAnalyticsPlugin }]) => {
      const clickPlugin = new ClickAnalyticsPlugin()

      appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          disableFetchTracking: false,
          enableAjaxPerfTracking: true,
          maxBatchInterval: 5000,
          extensions: [clickPlugin],
          extensionConfig: {
            [clickPlugin.identifier]: {
              autoCapture: true,
              dataTags: { useDefaultContentNameOrId: false },
            },
          },
        },
      })
      appInsights.loadAppInsights()

      appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.baseType === 'ExceptionData') {
          const exceptions = (envelope.baseData as { exceptions?: { message?: string }[] })?.exceptions
          if (exceptions?.some((e) => e.message === 'canceled' || e.message?.includes('ERR_CANCELED'))) return false
        }
      })

      appInsights.trackPageView()
    })
    .catch(() => {
      // Telemetry is non-critical — silently swallow import failures
    })
}

export function setAuthenticatedUser(userId: string, accountId?: string) {
  if (authenticatedUserId === userId) return
  authenticatedUserId = userId

  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true)
    return
  }

  // Queue until App Insights initializes
  readyPromise?.then(() => {
    appInsights?.setAuthenticatedUserContext(userId, accountId, true)
  })
}

export function trackException(error: unknown, properties?: Record<string, string>) {
  if (!appInsights) return
  // Reported once as the 'auth_session_expired' event; every failed call after
  // it would otherwise flood exceptions (24 in 1.3s from one tab).
  if (error instanceof SessionExpiredError) return
  const err = error instanceof Error ? error : new Error(String(error))
  appInsights.trackException({ exception: err }, withQuoteId(properties))
}

export function trackEvent(name: string, properties?: Record<string, string>) {
  if (!appInsights) return
  appInsights.trackEvent({ name }, withQuoteId(properties))
}
